// src/app/components/foundation/Workspace.tsx
import { useEffect, useRef, useState, type ReactNode, type TouchEvent as ReactTouchEvent } from 'react';

export interface WorkspaceProps {
  /** Called on backdrop click or Escape. Mount/unmount the Workspace to open/close it. */
  onClose: () => void;
  /** `aria-label` for the dialog panel. */
  ariaLabel: string;
  /** `aria-label` for the backdrop close button. */
  closeLabel: string;
  children: ReactNode;
}

// Matches the panel transition duration below -- the actual unmount (via
// `onClose`) is deferred by this long so the slide-out animation gets a
// chance to play before the caller removes the Workspace from the tree.
const CLOSE_ANIMATION_MS = 300;

// Minimum horizontal drag distance (px) for a touch swipe to count as a
// close gesture. Only relevant on touch devices -- these handlers simply
// never fire from mouse/pointer interaction.
const SWIPE_CLOSE_THRESHOLD_PX = 80;

/**
 * Generic slide-over workspace shell: a click-to-close backdrop plus a
 * `role="dialog"` panel anchored to the left edge of the screen, closable
 * with Escape. Extracted from the Orders feature's order-detail panel so any
 * feature needing a side panel (order/customer/ticket detail, etc.) can
 * reuse the same shell -- callers own everything inside (`children`),
 * this owns only the backdrop/panel mechanics.
 *
 * Render this component only while the workspace should be open (mount
 * on open, unmount on close) -- the Escape-key listener attaches for as
 * long as it stays mounted. Opening/closing itself is still driven by that
 * mount/unmount from the caller; internally the panel just plays a
 * horizontal slide transition (translateX only, no opacity/fade, no
 * bounce/scale/blur) on mount and briefly delays the `onClose` call so the
 * same slide can play in reverse before unmount happens -- matching the
 * `transition-transform duration-300 ease-in-out` motion used by
 * `MobileNavDrawer`. The enter transition is kicked off after two
 * animation frames rather than one, so the browser has definitely painted
 * the initial off-screen position before the transition starts --
 * otherwise the very first open can skip/collapse into a flash instead of
 * a slide.
 *
 * On touch devices, a dominantly-horizontal right-to-left swipe on the
 * panel past `SWIPE_CLOSE_THRESHOLD_PX` also closes it (via the same
 * `requestClose` path as the backdrop/Escape), while mostly-vertical drags
 * are ignored so normal scrolling inside the panel isn't hijacked. Mouse/
 * pointer interaction never triggers these handlers.
 */
export function Workspace({ onClose, ariaLabel, closeLabel, children }: WorkspaceProps) {
  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, []);

  function requestClose() {
    if (closing) return;
    setClosing(true);
    window.setTimeout(onClose, CLOSE_ANIMATION_MS);
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        requestClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, closing]);

  function handleTouchStart(event: ReactTouchEvent<HTMLDivElement>) {
    const touch = event.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }

  function handleTouchEnd(event: ReactTouchEvent<HTMLDivElement>) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    // Ignore mostly-vertical drags (scrolling) -- only a dominantly
    // horizontal, right-to-left swipe past the threshold closes the panel.
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (deltaX <= -SWIPE_CLOSE_THRESHOLD_PX) {
      requestClose();
    }
  }

  function handleTouchCancel() {
    touchStartRef.current = null;
  }

  const shown = entered && !closing;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label={closeLabel}
        onClick={requestClose}
        className="fixed inset-0 bg-black/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        className={`fixed inset-y-0 left-0 flex h-full w-full max-w-sm flex-col overflow-x-hidden border-r border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] bg-[var(--vania-surface-raised,#15161c)] shadow-[0_0_0_1px_var(--vania-colors-border-subtle,rgba(255,255,255,0.08)),24px_0_48px_-24px_var(--vania-colors-shadow,rgba(0,0,0,0.55))] transition-transform duration-300 ease-in-out ${
          shown ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
